import { useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Image as ImageIcon, Lock, Cloud, Check } from 'lucide-react';
import { SiteHeader } from '../../components/layout/SiteHeader';
import { Button } from '../../components/ui/Button';
import { Input, Label, Textarea } from '../../components/ui/Input';
import { Spinner } from '../../components/ui/Spinner';
import {
  listProductProfiles,
  createProject,
  getProductProfile,
  SQUARE_200_PRODUCT,
} from '@fotoboek/domain';
import { listTemplates, applyTemplateToPage } from '@fotoboek/templates';
import { useProjectStore } from '../../state/projectStore';
import { useAuthStore } from '../../state/authStore';
import { useUiStore } from '../../state/uiStore';
import { importPhotoFiles, type ImportedPhoto } from '../../photos/importPhotos';
import { migrateProjectToCloud } from '../../cloud/migrateToCloud';
import {
  createInitialWizardDraft,
  clampPageCountToProfile,
  validateTitle,
  canUseCloudLocation,
  WIZARD_STEP_KEYS,
} from './wizardHelpers';
import { t } from '../../i18n';
import './wizard-page.css';

const PRODUCT_PROFILES = listProductProfiles();
const DEFAULT_PRODUCT_PROFILE = PRODUCT_PROFILES[0] ?? SQUARE_200_PRODUCT;
const PAGE_TEMPLATES = listTemplates({ pageType: 'inner' });

export default function NewProjectWizardPage() {
  const navigate = useNavigate();
  const hydrateNewProject = useProjectStore((s) => s.hydrateNewProject);
  const authStatus = useAuthStore((s) => s.status);
  const pushToast = useUiStore((s) => s.pushToast);

  const [stepIndex, setStepIndex] = useState(0);
  const [draft, setDraft] = useState(() => createInitialWizardDraft(DEFAULT_PRODUCT_PROFILE));
  const [titleError, setTitleError] = useState<string | null>(null);
  const [pendingProjectId] = useState(() => crypto.randomUUID());
  const [importedPhotos, setImportedPhotos] = useState<ImportedPhoto[]>([]);
  const [importing, setImporting] = useState(false);
  const [creating, setCreating] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const profile = useMemo(
    () => getProductProfile(draft.productProfileId),
    [draft.productProfileId],
  );
  const stepKey = WIZARD_STEP_KEYS[stepIndex];

  const goNext = () => {
    if (stepKey === 'details') {
      const error = validateTitle(draft.title);
      setTitleError(error);
      if (error) return;
    }
    setStepIndex((i) => Math.min(WIZARD_STEP_KEYS.length - 1, i + 1));
  };
  const goPrev = () => setStepIndex((i) => Math.max(0, i - 1));

  const handlePhotoFiles = async (files: File[]) => {
    setImporting(true);
    const result = await importPhotoFiles(pendingProjectId, files);
    setImportedPhotos((prev) => [...prev, ...result.imported]);
    if (result.errors.length > 0) {
      pushToast({
        kind: 'error',
        message: `${result.errors.length} bestand(en) konden niet worden geïmporteerd.`,
      });
    }
    setImporting(false);
  };

  const handleCreate = async () => {
    setCreating(true);
    try {
      const location =
        draft.location === 'cloud' && canUseCloudLocation(authStatus) ? 'cloud' : 'local';
      let project = createProject({
        title: draft.title,
        description: draft.description || undefined,
        productProfileId: draft.productProfileId,
        pageCount: draft.pageCount,
        ownerMode: location,
      });

      if (draft.templateId) {
        project = {
          ...project,
          pages: project.pages.map((page) =>
            applyTemplateToPage(page, draft.templateId as string, { redistributePhotos: false }),
          ),
        };
      }

      project = {
        ...project,
        id: pendingProjectId,
        assets: importedPhotos.map((p) => p.asset),
      };

      const assetsRuntime = Object.fromEntries(
        importedPhotos.map((p) => [
          p.asset.id,
          {
            objectUrl: p.objectUrl,
            thumbnailUrl: p.thumbnailUrl,
            widthPx: p.asset.widthPx,
            heightPx: p.asset.heightPx,
          },
        ]),
      );

      await hydrateNewProject(project, assetsRuntime);

      if (location === 'cloud') {
        // Best-effort: the project already exists locally at this point, so
        // a failed/slow upload never blocks the user from starting to work —
        // autosync will retry the cloud migration from where it left off.
        const migrated = await migrateProjectToCloud(project);
        if (migrated.status !== 'ok') {
          pushToast({ kind: 'error', message: migrated.message });
        }
      }

      navigate(`/editor/${project.id}`);
    } catch (err) {
      pushToast({
        kind: 'error',
        message: err instanceof Error ? err.message : t('common.unknownError'),
      });
      setCreating(false);
    }
  };

  return (
    <div className="page-shell">
      <SiteHeader />
      <div className="wizard-page">
        <h1>{t('wizard.title')}</h1>
        <ol className="wizard-steps">
          {WIZARD_STEP_KEYS.map((key, i) => (
            <li key={key} className={i === stepIndex ? 'active' : i < stepIndex ? 'done' : ''}>
              <span className="wizard-step-dot">{i < stepIndex ? <Check size={12} /> : i + 1}</span>
              {t(`wizard.steps.${key}`)}
            </li>
          ))}
        </ol>

        <div className="wizard-panel">
          {stepKey === 'product' ? (
            <div className="wizard-step-content">
              <h2>{t('wizard.product.heading')}</h2>
              <div className="wizard-product-grid">
                {PRODUCT_PROFILES.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    className={
                      p.id === draft.productProfileId
                        ? 'wizard-product-card active'
                        : 'wizard-product-card'
                    }
                    onClick={() =>
                      setDraft((d) => ({
                        ...d,
                        productProfileId: p.id,
                        pageCount: getProductProfile(p.id).pageCount.default,
                      }))
                    }
                  >
                    <strong>{p.name}</strong>
                    <span>{p.description}</span>
                  </button>
                ))}
              </div>
              <div className="field-group">
                <Label htmlFor="pageCount">{t('wizard.product.pageCount')}</Label>
                <Input
                  id="pageCount"
                  type="number"
                  min={profile.pageCount.min}
                  max={profile.pageCount.max}
                  step={profile.pageCount.step}
                  value={draft.pageCount}
                  onChange={(e) =>
                    setDraft((d) => ({
                      ...d,
                      pageCount: clampPageCountToProfile(
                        Number(e.target.value) || profile.pageCount.min,
                        profile,
                      ),
                    }))
                  }
                />
                <span className="field-hint">
                  {t('wizard.product.pageCountHint', {
                    min: profile.pageCount.min,
                    max: profile.pageCount.max,
                    step: profile.pageCount.step,
                  })}
                </span>
              </div>
            </div>
          ) : null}

          {stepKey === 'details' ? (
            <div className="wizard-step-content">
              <h2>{t('wizard.details.heading')}</h2>
              <div className="field-group">
                <Label htmlFor="title">{t('wizard.details.titleLabel')}</Label>
                <Input
                  id="title"
                  autoFocus
                  placeholder={t('wizard.details.titlePlaceholder')}
                  value={draft.title}
                  onChange={(e) => setDraft((d) => ({ ...d, title: e.target.value }))}
                />
                {titleError ? <span className="field-error">{t(titleError)}</span> : null}
              </div>
              <div className="field-group">
                <Label htmlFor="description">
                  {t('wizard.details.descriptionLabel')}{' '}
                  <span className="field-hint">{t('common.optional')}</span>
                </Label>
                <Textarea
                  id="description"
                  placeholder={t('wizard.details.descriptionPlaceholder')}
                  value={draft.description}
                  onChange={(e) => setDraft((d) => ({ ...d, description: e.target.value }))}
                />
              </div>
            </div>
          ) : null}

          {stepKey === 'template' ? (
            <div className="wizard-step-content">
              <h2>{t('wizard.template.heading')}</h2>
              <div className="wizard-template-grid">
                <button
                  type="button"
                  className={
                    draft.templateId === null
                      ? 'wizard-template-card active'
                      : 'wizard-template-card'
                  }
                  onClick={() => setDraft((d) => ({ ...d, templateId: null }))}
                >
                  {t('wizard.template.blank')}
                </button>
                {PAGE_TEMPLATES.map((template) => (
                  <button
                    key={template.id}
                    type="button"
                    className={
                      draft.templateId === template.id
                        ? 'wizard-template-card active'
                        : 'wizard-template-card'
                    }
                    onClick={() => setDraft((d) => ({ ...d, templateId: template.id }))}
                  >
                    {template.name}
                  </button>
                ))}
              </div>
            </div>
          ) : null}

          {stepKey === 'photos' ? (
            <div className="wizard-step-content">
              <h2>{t('wizard.photos.heading')}</h2>
              <p className="wizard-step-body">{t('wizard.photos.body')}</p>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
                className="visually-hidden"
                onChange={(e) => {
                  const files = Array.from(e.target.files ?? []);
                  if (files.length) void handlePhotoFiles(files);
                  e.target.value = '';
                }}
              />
              <Button
                variant="secondary"
                onClick={() => fileInputRef.current?.click()}
                disabled={importing}
              >
                {importing ? <Spinner size={16} /> : <ImageIcon size={16} />}
                {t('wizard.photos.addPhotos')}
              </Button>
              {importedPhotos.length > 0 ? (
                <>
                  <p className="wizard-step-body">
                    {t('wizard.photos.selectedCount', { count: importedPhotos.length })}
                  </p>
                  <div className="wizard-photo-grid">
                    {importedPhotos.map((p) => (
                      <img key={p.asset.id} src={p.thumbnailUrl} alt="" />
                    ))}
                  </div>
                </>
              ) : null}
            </div>
          ) : null}

          {stepKey === 'location' ? (
            <div className="wizard-step-content">
              <h2>{t('wizard.location.heading')}</h2>
              <div className="wizard-location-grid">
                <button
                  type="button"
                  className={
                    draft.location === 'local'
                      ? 'wizard-location-card active'
                      : 'wizard-location-card'
                  }
                  onClick={() => setDraft((d) => ({ ...d, location: 'local' }))}
                >
                  <strong>{t('wizard.location.local')}</strong>
                  <span>{t('wizard.location.localHint')}</span>
                </button>
                <button
                  type="button"
                  className={
                    draft.location === 'cloud'
                      ? 'wizard-location-card active'
                      : 'wizard-location-card'
                  }
                  onClick={() => setDraft((d) => ({ ...d, location: 'cloud' }))}
                  disabled={!canUseCloudLocation(authStatus)}
                >
                  <strong>
                    <Cloud size={16} /> {t('wizard.location.cloud')}
                  </strong>
                  <span>{t('wizard.location.cloudHint')}</span>
                  {!canUseCloudLocation(authStatus) ? (
                    <span className="wizard-location-locked">
                      <Lock size={12} /> {t('wizard.location.loginRequired')}
                    </span>
                  ) : null}
                </button>
              </div>
            </div>
          ) : null}
        </div>

        <div className="wizard-footer">
          <Button variant="ghost" onClick={goPrev} disabled={stepIndex === 0}>
            {t('common.previous')}
          </Button>
          {stepIndex < WIZARD_STEP_KEYS.length - 1 ? (
            <Button variant="primary" onClick={goNext}>
              {t('common.next')}
            </Button>
          ) : (
            <Button variant="primary" onClick={handleCreate} disabled={creating}>
              {creating ? <Spinner size={16} /> : null}
              {creating ? t('wizard.creating') : t('wizard.create')}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
